import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Save, Layers, FileText, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionTitle, Field, Thumb, Pill } from "@/components/Bits";
import ProjectNav from "@/components/ProjectNav";
import ClientInvoiceBlocks from "@/components/ClientInvoiceBlocks";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";
const GROUPINGS = [
  ["space", "By Space / Room"],
  ["vendor", "By Vendor"],
  ["category", "By Product Category"],
  ["phase", "By Project Phase"],
  ["product", "Individual Products"],
  ["custom", "Custom Groups / Packages"],
];
const DISPLAY_MODES = [
  ["itemized", "Fully itemised", "Client sees each product line"],
  ["grouped", "Grouped summary", "Client sees one line per group"],
  ["summary", "Package total", "Client sees a title and total only"],
];
const VIS_FIELDS = [
  ["show_names", "Product names"],
  ["show_images", "Images"],
  ["show_descriptions", "Descriptions"],
  ["show_quantities", "Quantities"],
  ["show_vendors", "Vendor names"],
  ["show_unit_prices", "Unit prices"],
  ["show_links", "Product links"],
  ["show_subtotal", "Group subtotal"],
];
const STEPS = ["Selections", "Organise", "Preview"];
const ITEM_DISPLAY = [
  ["separate", "Show separately"],
  ["itemize_in_group", "Itemise within group"],
  ["in_group", "Include in group total"],
  ["internal_only", "Internal detail only"],
];
const defaultDisplay = (kind, groupMode) =>
  kind === "selection" ? (groupMode === "itemized" ? "itemize_in_group" : "in_group") : "separate";

const keyFor = (p, mode) => {
  if (mode === "space") return [p.space_id || "unassigned", p.space_name || "Unassigned"];
  if (mode === "vendor") return [p.vendor_id || "unassigned", p.vendor_name || "Other vendors"];
  if (mode === "category") return [p.category || "uncategorised", p.category || "Selections"];
  if (mode === "phase") return [p.phase || "general", p.phase || "Project selections"];
  if (mode === "product") return [p.product_id, p.name || "Selection"];
  return ["custom", "Design & procurement"];
};

export default function InvoiceBuilder() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [project, setProject] = useState(null);
  const [rows, setRows] = useState([]);
  const [chosen, setChosen] = useState({});
  const [step, setStep] = useState(0);
  const [grouping, setGrouping] = useState("space");
  const [groups, setGroups] = useState([]);
  const [assign, setAssign] = useState({});
  const [display, setDisplay] = useState({});
  const [meta, setMeta] = useState({ title: "", invoice_type: "Progress Payment", tax: 0, due_date: "", notes: "", internal_notes: "" });
  const [allowExtra, setAllowExtra] = useState(false);
  const [preview, setPreview] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [draftNumber, setDraftNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [extras, setExtras] = useState([]);
  const [addMenu, setAddMenu] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [libQuery, setLibQuery] = useState("");
  const [libCategory, setLibCategory] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState({ name: "", description: "", quantity: 1, unit_price: "", unit: "", discount: 0, unit_cost: "", taxable: true, save_to_library: false });

  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => {
      setProject(r.data);
      setMeta((m) => ({ ...m, title: m.title || `${r.data.name} — Furnishings` }));
    }).catch((e) => toast.error(apiError(e)));
    api.get(`/projects/${projectId}/invoiceable`).then((r) => {
      setRows(r.data);
      const picks = {};
      r.data.forEach((p) => {
        if (p.remaining_quantity > 0) picks[p.product_id] = { quantity: p.remaining_quantity, unit_price: p.unit_price };
      });
      setChosen(picks);
    }).catch((e) => toast.error(apiError(e)));
  }, [projectId]);

  const selected = rows.filter((r) => chosen[r.product_id]);

  useEffect(() => {
    if (libraryOpen && !libraryItems.length) {
      api.get("/invoice-library").then((r) => setLibraryItems(r.data)).catch(() => {});
    }
  }, [libraryOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const addFromLibrary = (item) => {
    setExtras((list) => [
      ...list,
      {
        id: `extra-${Date.now()}-${list.length}`,
        kind: "library",
        library_item_id: item.id,
        name: item.name,
        description: item.description || "",
        quantity: item.default_quantity ?? 1,
        unit: item.unit || "",
        pricing_type: item.pricing_type || "flat",
        unit_price: item.default_rate ?? "",
        unit_cost: item.internal_cost ?? "",
        discount: 0,
        taxable: item.taxable !== false,
        category: item.category || null,
        group_id: null,
      },
    ]);
    toast.success(`${item.name} added — edit the amount for this invoice only`);
  };

  const addCustom = async () => {
    if (!custom.name.trim()) {
      toast.error("Give the charge a name.");
      return;
    }
    if (custom.save_to_library && isAdmin) {
      // Saved separately from this invoice's own line, so later edits stay independent.
      await api
        .post("/invoice-library", {
          name: custom.name.trim(),
          description: custom.description || null,
          category: "Other",
          default_rate: custom.unit_price === "" ? null : Number(custom.unit_price),
          pricing_type: "flat",
          default_quantity: Number(custom.quantity) || 1,
          unit: custom.unit || null,
          taxable: custom.taxable,
        })
        .then(() => toast.success("Also saved to your invoice library"))
        .catch((e) => toast.message(apiError(e)));
    }
    setExtras((list) => [
      ...list,
      { ...custom, id: `extra-${Date.now()}-${list.length}`, kind: "custom", pricing_type: "flat", library_item_id: null, group_id: null },
    ]);
    setCustom({ name: "", description: "", quantity: 1, unit_price: "", unit: "", discount: 0, unit_cost: "", taxable: true, save_to_library: false });
    setCustomOpen(false);
  };

  const extraAmount = (x) =>
    x.pricing_type === "percentage"
      ? ((Number(x.unit_price) || 0) / 100) * selectionSubtotal - (Number(x.discount) || 0)
      : (Number(x.quantity) || 0) * (Number(x.unit_price) || 0) - (Number(x.discount) || 0);

  // Groups rebuild whenever the grouping changes; money never moves, only presentation.
  const buildGroups = (mode) => {
    const map = new Map();
    const nextAssign = {};
    selected.forEach((p) => {
      const [key, title] = keyFor(p, mode);
      if (!map.has(key)) {
        map.set(key, { id: key, title, description: "", display_mode: "itemized", order: map.size, visibility: {} });
      }
      nextAssign[p.product_id] = key;
    });
    setGroups([...map.values()]);
    setAssign((a) => ({ ...a, ...nextAssign }));
  };

  const addGroup = () => {
    const id = `group-${Date.now()}`;
    setGroups((list) => [...list, { id, title: "New group", description: "", display_mode: "grouped", order: list.length, visibility: {} }]);
  };

  const removeGroup = (id) => {
    if (groups.length < 2) {
      toast.error("Keep at least one group.");
      return;
    }
    const rest = groups.filter((g) => g.id !== id);
    setGroups(rest);
    setAssign((a) => {
      const next = { ...a };
      Object.keys(next).forEach((k) => { if (next[k] === id) next[k] = rest[0].id; });
      return next;
    });
  };

  const goOrganise = () => {
    if (!selected.length) {
      toast.error("Choose at least one approved selection.");
      return;
    }
    buildGroups(grouping);
    setStep(1);
  };

  const selectionSubtotal = useMemo(
    () => selected.reduce((sum, p) => sum + (Number(chosen[p.product_id].quantity) || 0) * (Number(chosen[p.product_id].unit_price) || 0), 0),
    [selected, chosen]
  );

  const totals = useMemo(() => {
    const extrasClient = extras.reduce((sum, x) => sum + Math.max(extraAmount(x), 0), 0);
    const extrasCost = extras.reduce((sum, x) => sum + (Number(x.quantity) || 0) * (Number(x.unit_cost) || 0), 0);
    const client = selectionSubtotal + extrasClient;
    const cost = selected.reduce((sum, p) => sum + (Number(chosen[p.product_id].quantity) || 0) * (p.unit_cost || 0), 0) + extrasCost;
    return { client: Math.round(client * 100) / 100, cost: Math.round(cost * 100) / 100 };
  }, [selected, chosen, extras, selectionSubtotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = () => ({
    project_id: projectId,
    title: meta.title,
    invoice_type: meta.invoice_type,
    grouping,
    display_mode: "itemized",
    tax: Number(meta.tax) || 0,
    due_date: meta.due_date || null,
    notes: meta.notes || null,
    internal_notes: meta.internal_notes || null,
    allow_additional: allowExtra,
    groups: groups.map((g, i) => ({ ...g, order: i })),
    items: [
      ...selected.map((p) => ({
        group_id: assign[p.product_id] || groups[0]?.id,
        kind: "selection",
        client_display: display[p.product_id] || null,
        product_id: p.product_id,
        name: p.name || "Selection",
        description: p.description || null,
        quantity: Number(chosen[p.product_id].quantity) || 0,
        unit_price: Number(chosen[p.product_id].unit_price) || 0,
        unit_cost: p.unit_cost || 0,
        vendor_name: p.vendor_name || null,
        vendor_id: p.vendor_id || null,
        space_id: p.space_id || null,
        space_name: p.space_name || null,
        category: p.category || null,
        primary_image_file_id: p.primary_image_file_id || null,
        primary_image: p.primary_image || null,
        product_url: p.product_url || null,
      })),
      ...extras.map((x, xi) => ({
        group_id: assign[x.id] || groups[0]?.id,
        kind: x.kind,
        client_display: display[x.id] || "separate",
        order: 1000 + xi,
        library_item_id: x.library_item_id || null,
        name: x.name,
        description: x.description || null,
        quantity: Number(x.quantity) || 1,
        unit: x.unit || null,
        pricing_type: x.pricing_type || "flat",
        unit_price: Number(x.unit_price) || 0,
        unit_cost: Number(x.unit_cost) || 0,
        discount: Number(x.discount) || 0,
        taxable: x.taxable !== false,
        category: x.category || null,
      })),
    ],
  });

  const saveDraft = async (thenPreview) => {
    setBusy(true);
    try {
      // After the first save we keep amending that same draft, never creating a second one.
      let invoice = draftId;
      if (invoice) {
        const body = payload();
        const { data } = await api.patch(`/invoices/${invoice}`, {
          title: body.title, invoice_type: body.invoice_type, tax: body.tax,
          due_date: body.due_date, notes: body.notes, internal_notes: body.internal_notes,
          groups: body.groups, source_items: body.items,
        });
        invoice = data.id;
      } else {
        const { data } = await api.post("/invoices/from-selections", payload());
        invoice = data.id;
        setDraftId(data.id);
        setDraftNumber(data.number);
      }
      if (thenPreview) {
        const { data: view } = await api.get(`/invoices/${invoice}/client-preview`);
        setPreview({ ...view, invoice_id: invoice });
        setStep(2);
        toast.success(`Draft ${draftNumber || view.number} saved — nothing has been sent`);
      } else {
        toast.success("Draft saved");
        navigate(`/invoices/${invoice}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!project) return <div className="py-20 text-center text-sm text-fog">Loading…</div>;

  return (
    <div className="space-y-10 pb-10" data-testid="invoice-builder">
      <ProjectNav
        projectId={projectId}
        projectName={project.name}
        activeSection="Selections"
        trail={[
          { label: "Products / Sourcing", to: `/projects/${projectId}?tab=Selections` },
          { label: "Create Invoice" },
        ]}
      />
      {libraryOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-6" data-testid="library-picker">
          <div className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-t-sm bg-white p-6 sm:rounded-sm sm:p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <h3 className="font-serif text-2xl text-ink">Invoice item library</h3>
              <button type="button" data-testid="library-picker-close" onClick={() => setLibraryOpen(false)} className="rounded-sm border border-line p-1.5 text-stone hover:border-fog" aria-label="Close">
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex flex-wrap gap-3">
              <input data-testid="library-picker-search" value={libQuery} onChange={(e) => setLibQuery(e.target.value)} placeholder="Search…" className={`${input} sm:max-w-xs`} />
              <select data-testid="library-picker-category" value={libCategory} onChange={(e) => setLibCategory(e.target.value)} className={`${input} sm:max-w-xs`}>
                <option value="">All categories</option>
                {[...new Set(libraryItems.map((i) => i.category).filter(Boolean))].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="mt-6 divide-y divide-line">
              {libraryItems
                .filter((i) => (!libCategory || i.category === libCategory) && (!libQuery || i.name.toLowerCase().includes(libQuery.toLowerCase())))
                .map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`library-pick-${item.id}`}
                    onClick={() => addFromLibrary(item)}
                    className="flex w-full items-center justify-between gap-4 py-4 text-left hover:bg-bone"
                  >
                    <span>
                      <span className="block text-sm text-ink">{item.name}</span>
                      <span className="block text-xs text-fog">{item.category || "Uncategorised"}{item.unit ? ` · ${item.unit}` : ""}</span>
                    </span>
                    <span className="num text-sm text-ink">
                      {item.default_rate === null || item.default_rate === undefined
                        ? "Price on use"
                        : item.pricing_type === "percentage"
                        ? `${item.default_rate}%`
                        : money(item.default_rate)}
                    </span>
                  </button>
                ))}
              {libraryItems.length === 0 && (
                <p className="py-8 text-center text-sm text-fog">
                  Your library is empty. Add reusable charges under Invoice Items.
                </p>
              )}
            </div>
            <button type="button" onClick={() => setLibraryOpen(false)} className="mt-7 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Done</button>
          </div>
        </div>
      )}

      {customOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-6" data-testid="custom-item-dialog">
          <div className="max-h-[88vh] w-full max-w-xl overflow-y-auto rounded-t-sm bg-white p-6 sm:rounded-sm sm:p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <h3 className="font-serif text-2xl text-ink">Custom line item</h3>
              <button type="button" data-testid="custom-item-close" onClick={() => setCustomOpen(false)} className="rounded-sm border border-line p-1.5 text-stone hover:border-fog" aria-label="Close">
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Name *" className="sm:col-span-2">
                <input data-testid="custom-name-input" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} className={input} />
              </Field>
              <Field label="Description" className="sm:col-span-2">
                <input data-testid="custom-desc-input" value={custom.description} onChange={(e) => setCustom({ ...custom, description: e.target.value })} className={input} />
              </Field>
              <Field label="Quantity">
                <input data-testid="custom-qty-input" type="number" step="0.5" min="0" value={custom.quantity} onChange={(e) => setCustom({ ...custom, quantity: e.target.value })} className={`${input} num`} />
              </Field>
              <Field label="Unit price">
                <input data-testid="custom-price-input" type="number" step="0.01" min="0" value={custom.unit_price} onChange={(e) => setCustom({ ...custom, unit_price: e.target.value })} className={`${input} num`} />
              </Field>
              <Field label="Unit (each, hours…)">
                <input data-testid="custom-unit-input" value={custom.unit} onChange={(e) => setCustom({ ...custom, unit: e.target.value })} className={input} />
              </Field>
              {isAdmin && (
                <Field label="Internal cost (optional)">
                  <input data-testid="custom-cost-input" type="number" step="0.01" min="0" value={custom.unit_cost} onChange={(e) => setCustom({ ...custom, unit_cost: e.target.value })} className={`${input} num`} />
                </Field>
              )}
              {isAdmin && (
                <label className="flex items-center gap-2 text-sm text-stone sm:col-span-2">
                  <input type="checkbox" data-testid="custom-save-library" checked={custom.save_to_library} onChange={(e) => setCustom({ ...custom, save_to_library: e.target.checked })} />
                  Save this item to my Invoice Item Library for future use
                </label>
              )}
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <button type="button" data-testid="custom-add-btn" onClick={addCustom} className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Add to invoice</button>
              <button type="button" onClick={() => setCustomOpen(false)} className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <PageHeader
        overline="Billing"
        title="Create Invoice from Approved Selections"
        subtitle="Nothing is sent to your client — this saves a draft you can review and issue when ready."
      />

      <div className="flex gap-6 border-b border-line pb-3 text-sm">
        {STEPS.map((label, i) => (
          <button
            key={label}
            type="button"
            data-testid={`builder-step-${i}`}
            onClick={() => i < step && setStep(i)}
            className={i === step ? "font-medium text-ink" : "text-stone hover:text-ink"}
          >
            {i + 1}. {label}
          </button>
        ))}
      </div>

      {step === 0 && (
        <section className="space-y-6" data-testid="builder-selections">
          {rows.length === 0 ? (
            <div className="card-surface p-10 text-center text-sm text-fog">
              No approved selections on this project yet.
            </div>
          ) : (
            <>
              <div className="card-surface overflow-x-auto">
                <table className="w-full min-w-[46rem] text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs uppercase tracking-[0.12em] text-stone">
                      <th className="p-4">Selection</th>
                      <th className="p-4">Space</th>
                      <th className="p-4">Billed</th>
                      <th className="p-4 num">Qty</th>
                      <th className="p-4 num">Client price</th>
                      <th className="p-4 num">Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => {
                      const pick = chosen[p.product_id];
                      return (
                        <tr key={p.product_id} className="border-b border-line/60" data-testid={`invoiceable-${p.product_id}`}>
                          <td className="p-4">
                            <label className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                data-testid={`pick-${p.product_id}`}
                                checked={Boolean(pick)}
                                onChange={(e) =>
                                  setChosen((c) => {
                                    const next = { ...c };
                                    if (e.target.checked) next[p.product_id] = { quantity: p.remaining_quantity || p.quantity, unit_price: p.unit_price };
                                    else delete next[p.product_id];
                                    return next;
                                  })
                                }
                              />
                              <Thumb src={productImage(p)} className="h-10 w-12" />
                              <span>
                                <span className="block text-ink">{p.name}</span>
                                <span className="block text-xs text-fog">{p.vendor_name || "No vendor"}</span>
                              </span>
                            </label>
                          </td>
                          <td className="p-4 text-stone">{p.space_name || "—"}</td>
                          <td className="p-4">
                            <Pill tone={p.invoice_state === "full" ? "bg-line text-stone" : p.invoice_state === "partial" ? "bg-[#EDEFC4] text-ink" : "bg-white text-stone"}>
                              {p.invoice_state === "full" ? "Fully invoiced" : p.invoice_state === "partial" ? `${p.invoiced_quantity} of ${p.quantity}` : "Not invoiced"}
                            </Pill>
                          </td>
                          <td className="p-4 num">
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              data-testid={`qty-${p.product_id}`}
                              disabled={!pick}
                              value={pick?.quantity ?? ""}
                              onChange={(e) => setChosen((c) => ({ ...c, [p.product_id]: { ...c[p.product_id], quantity: e.target.value } }))}
                              className={`${input} w-20 num`}
                            />
                          </td>
                          <td className="p-4 num">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              data-testid={`price-${p.product_id}`}
                              disabled={!pick}
                              value={pick?.unit_price ?? ""}
                              onChange={(e) => setChosen((c) => ({ ...c, [p.product_id]: { ...c[p.product_id], unit_price: e.target.value } }))}
                              className={`${input} w-28 num`}
                            />
                          </td>
                          <td className="p-4 num text-ink">
                            {pick ? money((Number(pick.quantity) || 0) * (Number(pick.unit_price) || 0)) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-5">
                <label className="flex items-center gap-3 text-sm text-stone">
                  <input type="checkbox" data-testid="allow-extra" checked={allowExtra} onChange={(e) => setAllowExtra(e.target.checked)} />
                  Bill extra beyond the approved quantity
                </label>
                <button
                  type="button"
                  data-testid="builder-next-organise"
                  onClick={goOrganise}
                  className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark"
                >
                  <Layers className="h-4 w-4" strokeWidth={1.5} /> Choose grouping
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {step === 1 && (
        <section className="space-y-7" data-testid="builder-organise">
          <div className="card-surface p-7">
            <SectionTitle>Invoice details</SectionTitle>
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Invoice Title">
                <input data-testid="invoice-title-input" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className={input} />
              </Field>
              <Field label="Grouping">
                <select
                  data-testid="grouping-select"
                  value={grouping}
                  onChange={(e) => { setGrouping(e.target.value); buildGroups(e.target.value); }}
                  className={input}
                >
                  {GROUPINGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="Tax">
                <input data-testid="invoice-tax-input" type="number" step="0.01" min="0" value={meta.tax} onChange={(e) => setMeta({ ...meta, tax: e.target.value })} className={`${input} num`} />
              </Field>
              <Field label="Due Date">
                <input data-testid="invoice-due-input" type="date" value={meta.due_date} onChange={(e) => setMeta({ ...meta, due_date: e.target.value })} className={input} />
              </Field>
              <Field label="Client Note" className="sm:col-span-2">
                <textarea data-testid="invoice-notes-input" rows={2} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} className={input} />
              </Field>
            </div>
          </div>

          {groups.map((g, gi) => (
            <div key={g.id} className="card-surface p-7" data-testid={`group-card-${g.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-[14rem] flex-1">
                  <Field label="Group Title">
                    <input
                      data-testid={`group-title-${g.id}`}
                      value={g.title}
                      onChange={(e) => setGroups((list) => list.map((x, i) => (i === gi ? { ...x, title: e.target.value } : x)))}
                      className={input}
                    />
                  </Field>
                </div>
                <div className="flex gap-2 pt-7">
                  <button type="button" data-testid={`group-up-${g.id}`} disabled={gi === 0} onClick={() => setGroups((l) => { const n = [...l]; [n[gi - 1], n[gi]] = [n[gi], n[gi - 1]]; return n; })} className="rounded-sm border border-line px-3 py-2 text-xs text-stone disabled:opacity-40">↑</button>
                  <button type="button" data-testid={`group-down-${g.id}`} disabled={gi === groups.length - 1} onClick={() => setGroups((l) => { const n = [...l]; [n[gi + 1], n[gi]] = [n[gi], n[gi + 1]]; return n; })} className="rounded-sm border border-line px-3 py-2 text-xs text-stone disabled:opacity-40">↓</button>
                  <button type="button" data-testid={`group-remove-${g.id}`} onClick={() => removeGroup(g.id)} className="rounded-sm border border-line px-3 py-2 text-xs text-stone hover:border-clay hover:text-clay">Remove</button>
                </div>
              </div>

              <Field label="Group Description (shown on grouped invoices)" className="mt-4">
                <input
                  data-testid={`group-desc-${g.id}`}
                  value={g.description || ""}
                  onChange={(e) => setGroups((list) => list.map((x, i) => (i === gi ? { ...x, description: e.target.value } : x)))}
                  className={input}
                />
              </Field>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {DISPLAY_MODES.map(([mode, label, hint]) => (
                  <button
                    key={mode}
                    type="button"
                    data-testid={`group-mode-${g.id}-${mode}`}
                    onClick={() => setGroups((list) => list.map((x, i) => (i === gi ? { ...x, display_mode: mode, visibility: {} } : x)))}
                    className={`rounded-sm border px-4 py-3 text-left transition-colors ${g.display_mode === mode ? "border-lime bg-[#F7F8E8]" : "border-line hover:border-fog"}`}
                  >
                    <span className="block text-sm text-ink">{label}</span>
                    <span className="block text-xs text-fog">{hint}</span>
                  </button>
                ))}
              </div>

              {g.display_mode === "itemized" && (
                <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3" data-testid={`group-visibility-${g.id}`}>
                  {VIS_FIELDS.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-xs text-stone">
                      <input
                        type="checkbox"
                        data-testid={`vis-${g.id}-${key}`}
                        checked={g.visibility?.[key] ?? ["show_names", "show_descriptions", "show_quantities", "show_unit_prices", "show_subtotal"].includes(key)}
                        onChange={(e) => setGroups((list) => list.map((x, i) => (i === gi ? { ...x, visibility: { ...x.visibility, [key]: e.target.checked } } : x)))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              )}

              {g.display_mode !== "itemized" && (
                <label className="mt-5 flex items-center gap-2 text-xs text-stone" data-testid={`group-subtotal-${g.id}`}>
                  <input
                    type="checkbox"
                    data-testid={`vis-${g.id}-show_subtotal`}
                    checked={g.visibility?.show_subtotal !== false}
                    onChange={(e) => setGroups((list) => list.map((x, i) => (i === gi ? { ...x, visibility: { ...x.visibility, show_subtotal: e.target.checked } } : x)))}
                  />
                  Show this group's total on the client invoice
                </label>
              )}

              <ul className="mt-5 space-y-3 border-t border-line pt-4 text-sm">
                {selected.filter((p) => (assign[p.product_id] || groups[0]?.id) === g.id).map((p) => (
                  <li key={p.product_id} className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-stone">{p.name}</span>
                    <span className="flex flex-wrap items-center gap-3">
                      <span className="num text-ink">{money((Number(chosen[p.product_id].quantity) || 0) * (Number(chosen[p.product_id].unit_price) || 0))}</span>
                      <select
                        data-testid={`item-display-${p.product_id}`}
                        value={display[p.product_id] || defaultDisplay("selection", g.display_mode)}                        onChange={(e) => setDisplay((d) => ({ ...d, [p.product_id]: e.target.value }))}
                        className="rounded-sm border border-line bg-white px-2 py-1 text-xs"
                      >
                        {ITEM_DISPLAY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      <select
                        data-testid={`move-${p.product_id}`}
                        value={assign[p.product_id] || groups[0]?.id}
                        onChange={(e) => setAssign((a) => ({ ...a, [p.product_id]: e.target.value }))}
                        className="rounded-sm border border-line bg-white px-2 py-1 text-xs"
                      >
                        {groups.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                      </select>
                    </span>
                  </li>
                ))}
                {extras.filter((x) => (assign[x.id] || groups[0]?.id) === g.id && (display[x.id] || "separate") !== "separate").map((x) => (
                  <li key={x.id} className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-stone">{x.name} <span className="text-fog">· charge</span></span>
                    <span className="num text-ink">{money(Math.max(extraAmount(x), 0))}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <button type="button" data-testid="add-group-btn" onClick={addGroup}
            className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:border-lime">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Add a custom group
          </button>

          <div className="card-surface p-7" data-testid="extra-items">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <SectionTitle>Services & other charges</SectionTitle>
              <div className="relative">
                <button
                  type="button"
                  data-testid="add-item-btn"
                  onClick={() => setAddMenu((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-sm text-ink transition-colors hover:border-lime"
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Item
                </button>
                {addMenu && (
                  <div className="absolute right-0 z-20 mt-2 w-60 rounded-sm border border-line bg-white py-1 shadow-sm" data-testid="add-item-menu">
                    <button type="button" data-testid="add-from-library" onClick={() => { setAddMenu(false); setLibraryOpen(true); }} className="block w-full px-4 py-2.5 text-left text-sm text-ink hover:bg-bone">
                      From Invoice Item Library
                    </button>
                    <button type="button" data-testid="add-custom-item" onClick={() => { setAddMenu(false); setCustomOpen(true); }} className="block w-full px-4 py-2.5 text-left text-sm text-ink hover:bg-bone">
                      Custom Line Item
                    </button>
                    <button type="button" data-testid="add-from-selections" onClick={() => { setAddMenu(false); setStep(0); }} className="block w-full px-4 py-2.5 text-left text-sm text-ink hover:bg-bone">
                      From Approved Selections
                    </button>
                  </div>
                )}
              </div>
            </div>

            {extras.length === 0 ? (
              <p className="mt-4 text-sm text-fog">No services or extra charges yet — add a design fee, installation or delivery.</p>
            ) : (
              <div className="mt-5 space-y-5">
                {extras.map((x, xi) => (
                  <div key={x.id} className="rounded-sm border border-line p-5" data-testid={`extra-item-${xi}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <Pill tone={x.kind === "library" ? "bg-[#EDEFC4] text-ink" : "bg-line text-stone"}>
                        {x.kind === "library" ? "Library item" : "Custom charge"}
                      </Pill>
                      <button type="button" data-testid={`extra-remove-${xi}`} onClick={() => setExtras((l) => l.filter((_, i) => i !== xi))} className="text-xs text-stone hover:text-clay">
                        Remove
                      </button>
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <Field label="Name">
                        <input data-testid={`extra-name-${xi}`} value={x.name} onChange={(e) => setExtras((l) => l.map((v, i) => (i === xi ? { ...v, name: e.target.value } : v)))} className={input} />
                      </Field>
                      <Field label="Client display">
                        <select data-testid={`extra-display-${xi}`} value={display[x.id] || "separate"} onChange={(e) => setDisplay((d) => ({ ...d, [x.id]: e.target.value }))} className={input}>
                          {ITEM_DISPLAY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </Field>
                      <Field label="Group">
                        <select data-testid={`extra-group-${xi}`} value={assign[x.id] || groups[0]?.id} onChange={(e) => setAssign((a) => ({ ...a, [x.id]: e.target.value }))} className={input} disabled={(display[x.id] || "separate") === "separate"}>
                          {groups.map((grp) => <option key={grp.id} value={grp.id}>{grp.title}</option>)}
                        </select>
                      </Field>
                      <Field label="Description" className="sm:col-span-2">
                        <input data-testid={`extra-desc-${xi}`} value={x.description || ""} onChange={(e) => setExtras((l) => l.map((v, i) => (i === xi ? { ...v, description: e.target.value } : v)))} className={input} />
                      </Field>
                      <Field label="Quantity">
                        <input data-testid={`extra-qty-${xi}`} type="number" step="0.5" min="0" disabled={x.pricing_type === "percentage"} value={x.quantity} onChange={(e) => setExtras((l) => l.map((v, i) => (i === xi ? { ...v, quantity: e.target.value } : v)))} className={`${input} num`} />
                      </Field>
                      <Field label={x.pricing_type === "percentage" ? "Percentage of selections" : "Unit price"}>
                        <input data-testid={`extra-price-${xi}`} type="number" step="0.01" min="0" placeholder="Enter amount" value={x.unit_price} onChange={(e) => setExtras((l) => l.map((v, i) => (i === xi ? { ...v, unit_price: e.target.value } : v)))} className={`${input} num`} />
                      </Field>
                      <Field label="Discount (optional)">
                        <input data-testid={`extra-discount-${xi}`} type="number" step="0.01" min="0" value={x.discount} onChange={(e) => setExtras((l) => l.map((v, i) => (i === xi ? { ...v, discount: e.target.value } : v)))} className={`${input} num`} />
                      </Field>
                      {isAdmin && (
                        <Field label="Internal cost (optional)">
                          <input data-testid={`extra-cost-${xi}`} type="number" step="0.01" min="0" value={x.unit_cost} onChange={(e) => setExtras((l) => l.map((v, i) => (i === xi ? { ...v, unit_cost: e.target.value } : v)))} className={`${input} num`} />
                        </Field>
                      )}
                    </div>
                    <p className="mt-3 text-right text-sm text-ink" data-testid={`extra-amount-${xi}`}>
                      Line total <span className="num">{money(Math.max(extraAmount(x), 0))}</span>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="card-surface p-7" data-testid="internal-summary">
              <SectionTitle>Internal summary</SectionTitle>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
                <div><span className="overline block">Sourcing cost</span><span className="num text-lg text-ink">{money(totals.cost)}</span></div>
                <div><span className="overline block">Client billing</span><span className="num text-lg text-ink">{money(totals.client)}</span></div>
                <div><span className="overline block">Gross profit</span><span className="num text-lg text-lime-dark">{money(totals.client - totals.cost)}</span></div>
                <div><span className="overline block">Margin</span><span className="num text-lg text-ink">{totals.client ? Math.round(((totals.client - totals.cost) / totals.client) * 1000) / 10 : 0}%</span></div>
              </div>
              <p className="mt-4 text-xs text-fog">Costs, profit and margin are never sent to the client.</p>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button type="button" data-testid="builder-preview-btn" disabled={busy} onClick={() => saveDraft(true)} className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60">
              <Eye className="h-4 w-4" strokeWidth={1.5} /> {busy ? "Saving…" : "Save draft & preview as client"}
            </button>
            <button type="button" data-testid="builder-save-btn" disabled={busy} onClick={() => saveDraft(false)} className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-5 py-2.5 text-sm text-ink hover:border-lime">
              <Save className="h-4 w-4" strokeWidth={1.5} /> Save draft
            </button>
          </div>
        </section>
      )}

      {step === 2 && preview && (
        <section className="space-y-7" data-testid="builder-preview">
          <div className="card-surface p-7 sm:p-10">
            <span className="overline">Exactly what your client sees</span>
            <h2 className="mt-3 font-serif text-2xl text-ink">{preview.title}</h2>
            <p className="mt-1 text-xs text-fog">Invoice {preview.number} · {preview.project_name}</p>
            <div className="mt-7">
              <ClientInvoiceBlocks invoice={preview} testPrefix="preview-group" />
            </div>
            {preview.notes && <p className="mt-6 text-xs leading-relaxed text-stone">{preview.notes}</p>}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={`/invoices/${preview.invoice_id}`} data-testid="preview-open-invoice" className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
              <FileText className="h-4 w-4" strokeWidth={1.5} /> Open the draft
            </Link>
            <button type="button" onClick={() => setStep(1)} className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-5 py-2.5 text-sm text-ink hover:border-lime">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Keep editing
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
